import {
  makeObservable,
  runInAction,
  action,
  observable,
  computed,
} from "mobx";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { CacheKey } from "@/modules/cache/constants";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";

type PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: boolean;
  hasDismissedVoiceOnboardingSystemPermissions: boolean;
  hasCompletedOrSkippedNativeAppDownload: boolean;
  proactiveTrialOnboardingIsRelevant: boolean;
  hasCompletedOrSkippedProactiveTrialOnboarding: boolean;
  proactiveTrialOnboardingDebugOverride: boolean;
  memAgentOnboardingIsRelevant: boolean;
  hasCompletedMemAgentShortcutOnboarding: boolean;
  hasCompletedMemAgentFirstMessageOnboarding: boolean;
  quickThoughtsCaptureMethod: "voice" | "typed";
  existingUserMemAgentOnboardingDeferredUntilAccess: boolean;
  existingUserMemAgentOnboardingNeedsEvaluation: boolean;
  existingUserMemAgentOnboardingIsRelevant: boolean;
  hasCompletedExistingUserMemAgentIntroOnboarding: boolean;
  hasCompletedExistingUserMemAgentFirstMessageOnboarding: boolean;
};

const DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA: PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: false,
  hasDismissedVoiceOnboardingSystemPermissions: false,
  hasCompletedOrSkippedNativeAppDownload: false,
  proactiveTrialOnboardingIsRelevant: false,
  hasCompletedOrSkippedProactiveTrialOnboarding: false,
  proactiveTrialOnboardingDebugOverride: false,
  memAgentOnboardingIsRelevant: false,
  hasCompletedMemAgentShortcutOnboarding: false,
  hasCompletedMemAgentFirstMessageOnboarding: false,
  quickThoughtsCaptureMethod: "voice",
  existingUserMemAgentOnboardingDeferredUntilAccess: false,
  existingUserMemAgentOnboardingNeedsEvaluation: false,
  existingUserMemAgentOnboardingIsRelevant: false,
  hasCompletedExistingUserMemAgentIntroOnboarding: false,
  hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
};

export class PerDeviceOnboardingInfoObservable {
  store: AppStore;
  private deviceOnboardingInfo: AsyncResult<PerDeviceOnboardingInfoData> =
    asyncResultModule.setLoading();

  constructor({ store }: WithAppStore) {
    this.store = store;

    makeObservable<
      PerDeviceOnboardingInfoObservable,
      | "deviceOnboardingInfo"
      | "currentOnboardingSystemPermissionsData"
      | "shouldShowMeetingOnboardingSystemPermissions"
      | "shouldShowVoiceOnboardingSystemPermissions"
      | "hasCompletedOrSkippedNativeAppDownload"
      | "proactiveTrialOnboardingIsRelevant"
      | "hasCompletedOrSkippedProactiveTrialOnboarding"
      | "proactiveTrialOnboardingDebugOverride"
      | "memAgentOnboardingIsRelevant"
      | "hasCompletedMemAgentShortcutOnboarding"
      | "hasCompletedMemAgentFirstMessageOnboarding"
      | "quickThoughtsCaptureMethod"
      | "existingUserMemAgentOnboardingDeferredUntilAccess"
      | "existingUserMemAgentOnboardingNeedsEvaluation"
      | "existingUserMemAgentOnboardingIsRelevant"
      | "hasCompletedExistingUserMemAgentIntroOnboarding"
      | "hasCompletedExistingUserMemAgentFirstMessageOnboarding"
      | "completeOrSkipNativeAppDownload"
      | "activateMemAgentFirstMessageOnboarding"
      | "setQuickThoughtsCaptureMethod"
      | "_resolveOnboardingInfoDataBasedOnDeviceState"
      | "_hydrateOnboardingInfo"
    >(this, {
      store: false,
      deviceOnboardingInfo: observable,
      shouldShowMeetingOnboardingSystemPermissions: computed,
      shouldShowVoiceOnboardingSystemPermissions: computed,
      currentOnboardingSystemPermissionsData: computed,
      _resolveOnboardingInfoDataBasedOnDeviceState: action,
      _hydrateOnboardingInfo: false,
      initialize: action,
      resetState: action,
      dismissMeetingOnboardingSystemPermissions: action,
      dismissVoiceOnboardingSystemPermissions: action,
      debugResetMeetingPermissions: action,
      debugResetVoicePermissions: action,
      debugResetNativeAppDownload: action,
      hasCompletedOrSkippedNativeAppDownload: computed,
      proactiveTrialOnboardingIsRelevant: computed,
      hasCompletedOrSkippedProactiveTrialOnboarding: computed,
      proactiveTrialOnboardingDebugOverride: computed,
      memAgentOnboardingIsRelevant: computed,
      hasCompletedMemAgentShortcutOnboarding: computed,
      hasCompletedMemAgentFirstMessageOnboarding: computed,
      quickThoughtsCaptureMethod: computed,
      existingUserMemAgentOnboardingDeferredUntilAccess: computed,
      existingUserMemAgentOnboardingNeedsEvaluation: computed,
      existingUserMemAgentOnboardingIsRelevant: computed,
      hasCompletedExistingUserMemAgentIntroOnboarding: computed,
      hasCompletedExistingUserMemAgentFirstMessageOnboarding: computed,
      completeOrSkipNativeAppDownload: action,
      activateProactiveTrialOnboarding: action,
      completeOrSkipProactiveTrialOnboarding: action,
      debugResetProactiveTrialOnboarding: action,
      activateMemAgentOnboarding: action,
      activateMemAgentFirstMessageOnboarding: action,
      setQuickThoughtsCaptureMethod: action,
      completeMemAgentShortcutOnboarding: action,
      completeMemAgentFirstMessageOnboarding: action,
      debugResetMemAgentOnboarding: action,
      activateExistingUserMemAgentOnboarding: action,
      deferExistingUserMemAgentOnboardingUntilAccess: action,
      dismissExistingUserMemAgentOnboarding: action,
      completeExistingUserMemAgentIntroOnboarding: action,
      completeExistingUserMemAgentFirstMessageOnboarding: action,
      debugResetExistingUserMemAgentOnboarding: action,
    });
  }

  /**
   * Note that we use the `inverse` here instead of hasDismissedOnboardingSystemPermissions`.
   *
   * This is because:
   * - If we hit a snag when hydrating, we want to default to -not- showing the onboarding steps.
   */
  get shouldShowMeetingOnboardingSystemPermissions(): boolean {
    if (!this.deviceOnboardingInfo.data) return false;
    if (
      !this.deviceOnboardingInfo.data
        .hasDismissedMeetingOnboardingSystemPermissions
    ) {
      return true;
    }

    const enabled = this.store.systemPermissions.meetingNotificationEnabled;
    if (!enabled) return false;

    // Do we have the required permissions?
    return !this.store.systemPermissions.hasRequiredMeetingPermissions;
  }

  get shouldShowVoiceOnboardingSystemPermissions(): boolean {
    if (!this.deviceOnboardingInfo.data) return false;
    return !this.deviceOnboardingInfo.data
      .hasDismissedVoiceOnboardingSystemPermissions;
  }

  get hasCompletedOrSkippedNativeAppDownload(): boolean {
    return (
      this.deviceOnboardingInfo.data?.hasCompletedOrSkippedNativeAppDownload ??
      false
    );
  }

  get proactiveTrialOnboardingIsRelevant(): boolean {
    return (
      this.deviceOnboardingInfo.data?.proactiveTrialOnboardingIsRelevant ??
      false
    );
  }

  get hasCompletedOrSkippedProactiveTrialOnboarding(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.hasCompletedOrSkippedProactiveTrialOnboarding ?? false
    );
  }

  get proactiveTrialOnboardingDebugOverride(): boolean {
    return (
      this.deviceOnboardingInfo.data?.proactiveTrialOnboardingDebugOverride ??
      false
    );
  }

  get memAgentOnboardingIsRelevant(): boolean {
    const cachedMemAgentOnboardingIsRelevant =
      this.deviceOnboardingInfo.data?.memAgentOnboardingIsRelevant ?? false;
    if (!cachedMemAgentOnboardingIsRelevant) {
      return false;
    }

    // Proactive subscribers promoted from chat-only access can carry a stale
    // generic Mem Agent onboarding flag. Existing-user onboarding owns them.
    if (
      this.store.subscriptions.hasProactiveAccess &&
      !this.hasCompletedOrSkippedProactiveTrialOnboarding
    ) {
      return false;
    }

    return true;
  }

  get hasCompletedMemAgentShortcutOnboarding(): boolean {
    return (
      this.deviceOnboardingInfo.data?.hasCompletedMemAgentShortcutOnboarding ??
      false
    );
  }

  get hasCompletedMemAgentFirstMessageOnboarding(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.hasCompletedMemAgentFirstMessageOnboarding ?? false
    );
  }

  get quickThoughtsCaptureMethod(): "voice" | "typed" {
    return (
      this.deviceOnboardingInfo.data?.quickThoughtsCaptureMethod ?? "voice"
    );
  }

  setQuickThoughtsCaptureMethod(method: "voice" | "typed") {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        quickThoughtsCaptureMethod: method,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  get existingUserMemAgentOnboardingDeferredUntilAccess(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.existingUserMemAgentOnboardingDeferredUntilAccess ?? false
    );
  }

  get existingUserMemAgentOnboardingNeedsEvaluation(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.existingUserMemAgentOnboardingNeedsEvaluation ?? false
    );
  }

  get existingUserMemAgentOnboardingIsRelevant(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.existingUserMemAgentOnboardingIsRelevant ?? false
    );
  }

  get hasCompletedExistingUserMemAgentIntroOnboarding(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.hasCompletedExistingUserMemAgentIntroOnboarding ?? false
    );
  }

  get hasCompletedExistingUserMemAgentFirstMessageOnboarding(): boolean {
    return (
      this.deviceOnboardingInfo.data
        ?.hasCompletedExistingUserMemAgentFirstMessageOnboarding ?? false
    );
  }

  completeOrSkipNativeAppDownload() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasCompletedOrSkippedNativeAppDownload: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  activateProactiveTrialOnboarding() {
    if (this.proactiveTrialOnboardingIsRelevant) return;

    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        proactiveTrialOnboardingIsRelevant: true,
        proactiveTrialOnboardingDebugOverride: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  completeOrSkipProactiveTrialOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        proactiveTrialOnboardingIsRelevant: true,
        hasCompletedOrSkippedProactiveTrialOnboarding: true,
        proactiveTrialOnboardingDebugOverride: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  activateMemAgentOnboarding() {
    if (this.memAgentOnboardingIsRelevant) return;

    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  activateMemAgentFirstMessageOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: true,
        hasCompletedMemAgentShortcutOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  completeMemAgentShortcutOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: true,
        hasCompletedMemAgentShortcutOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  completeMemAgentFirstMessageOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: true,
        hasCompletedMemAgentShortcutOnboarding: true,
        hasCompletedMemAgentFirstMessageOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetMemAgentOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: true,
        hasCompletedMemAgentShortcutOnboarding: false,
        hasCompletedMemAgentFirstMessageOnboarding: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /**
   * Enables the Mem Agent catch-up flow for a legacy device that already
   * completed the previous onboarding pipeline before these steps existed.
   */
  activateExistingUserMemAgentOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        memAgentOnboardingIsRelevant: false,
        hasCompletedMemAgentShortcutOnboarding: false,
        hasCompletedMemAgentFirstMessageOnboarding: false,
        existingUserMemAgentOnboardingDeferredUntilAccess: false,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: true,
        hasCompletedExistingUserMemAgentIntroOnboarding: false,
        hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /**
   * Keeps the catch-up flow pending for fully onboarded devices that only lack
   * floating Mem Agent access. Promotion can then activate the same real flow.
   */
  deferExistingUserMemAgentOnboardingUntilAccess() {
    if (this.existingUserMemAgentOnboardingDeferredUntilAccess) return;

    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        existingUserMemAgentOnboardingDeferredUntilAccess: true,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: false,
        hasCompletedExistingUserMemAgentIntroOnboarding: false,
        hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /** Records that the legacy-device catch-up flow does not apply on this device. */
  dismissExistingUserMemAgentOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        existingUserMemAgentOnboardingDeferredUntilAccess: false,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: false,
        hasCompletedExistingUserMemAgentIntroOnboarding: true,
        hasCompletedExistingUserMemAgentFirstMessageOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /** Advances the first catch-up screen while keeping the message gate active. */
  completeExistingUserMemAgentIntroOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        existingUserMemAgentOnboardingDeferredUntilAccess: false,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: true,
        hasCompletedExistingUserMemAgentIntroOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /** Completes the catch-up flow after the user sends a floating-agent message. */
  completeExistingUserMemAgentFirstMessageOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        existingUserMemAgentOnboardingDeferredUntilAccess: false,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: true,
        hasCompletedExistingUserMemAgentIntroOnboarding: true,
        hasCompletedExistingUserMemAgentFirstMessageOnboarding: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /** Makes the catch-up flow visible from debug onboarding reset tools. */
  debugResetExistingUserMemAgentOnboarding() {
    runInAction(() => {
      const nextData: PerDeviceOnboardingInfoData = {
        ...this.currentOnboardingSystemPermissionsData,
        existingUserMemAgentOnboardingDeferredUntilAccess: false,
        existingUserMemAgentOnboardingNeedsEvaluation: false,
        existingUserMemAgentOnboardingIsRelevant: true,
        hasCompletedExistingUserMemAgentIntroOnboarding: false,
        hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  async debugResetProactiveTrialOnboarding() {
    const nextData: PerDeviceOnboardingInfoData = {
      ...this.currentOnboardingSystemPermissionsData,
      proactiveTrialOnboardingIsRelevant: true,
      hasCompletedOrSkippedProactiveTrialOnboarding: false,
      proactiveTrialOnboardingDebugOverride: true,
      memAgentOnboardingIsRelevant: false,
      hasCompletedMemAgentShortcutOnboarding: false,
      hasCompletedMemAgentFirstMessageOnboarding: false,
      quickThoughtsCaptureMethod: "voice",
      existingUserMemAgentOnboardingDeferredUntilAccess: false,
      existingUserMemAgentOnboardingNeedsEvaluation: false,
      existingUserMemAgentOnboardingIsRelevant: false,
      hasCompletedExistingUserMemAgentIntroOnboarding: false,
      hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
    };

    runInAction(() => {
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });

    const didPersist = await this.store.accountCache.set(
      CacheKey.PerDeviceOnboardingInfo,
      nextData,
    );
    if (!didPersist) {
      throw new BaseError({
        message:
          "[PerDeviceOnboardingInfoObservable] Failed to persist Proactive trial debug reset",
      });
    }
  }

  private get currentOnboardingSystemPermissionsData(): PerDeviceOnboardingInfoData {
    return (
      this.deviceOnboardingInfo.data ?? DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA
    );
  }

  dismissMeetingOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedMeetingOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  dismissVoiceOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedVoiceOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetMeetingPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedMeetingOnboardingSystemPermissions: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetVoicePermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedVoiceOnboardingSystemPermissions: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  debugResetNativeAppDownload() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasCompletedOrSkippedNativeAppDownload: false,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /**
   * This determines the "initial" state for users.
   */
  private async _resolveOnboardingInfoDataBasedOnDeviceState(): Promise<PerDeviceOnboardingInfoData> {
    return {
      hasDismissedMeetingOnboardingSystemPermissions: false,
      hasDismissedVoiceOnboardingSystemPermissions: false,
      hasCompletedOrSkippedNativeAppDownload: false,
      proactiveTrialOnboardingIsRelevant: false,
      hasCompletedOrSkippedProactiveTrialOnboarding: false,
      proactiveTrialOnboardingDebugOverride: false,
      memAgentOnboardingIsRelevant: false,
      hasCompletedMemAgentShortcutOnboarding: false,
      hasCompletedMemAgentFirstMessageOnboarding: false,
      quickThoughtsCaptureMethod: "voice",
      existingUserMemAgentOnboardingDeferredUntilAccess: false,
      existingUserMemAgentOnboardingNeedsEvaluation: true,
      existingUserMemAgentOnboardingIsRelevant: false,
      hasCompletedExistingUserMemAgentIntroOnboarding: false,
      hasCompletedExistingUserMemAgentFirstMessageOnboarding: false,
    };
  }

  private async _hydrateOnboardingInfo() {
    try {
      let cachedData = await this.store.accountCache.get(
        CacheKey.PerDeviceOnboardingInfo,
      );

      // Migration path: if accountCache is empty, check globalCache and migrate
      if (!cachedData) {
        const globalCacheData = await this.store.publicAppStore.globalCache.get(
          CacheKey.PerDeviceOnboardingInfo,
        );

        if (globalCacheData) {
          this.store.accountCache.set(
            CacheKey.PerDeviceOnboardingInfo,
            globalCacheData,
          );
          cachedData = globalCacheData;

          logger.info({
            message:
              "[PerDeviceOnboardingInfoObservable] Migrated onboarding info from globalCache to accountCache",
          });
        }
      }

      if (!cachedData) {
        const resolvedData =
          await this._resolveOnboardingInfoDataBasedOnDeviceState();

        this.store.accountCache.set(
          CacheKey.PerDeviceOnboardingInfo,
          resolvedData,
        );
        this.deviceOnboardingInfo = asyncResultModule.setReady(resolvedData);

        return;
      }

      runInAction(() => {
        const hasLegacyExistingUserMemAgentOnboardingState =
          cachedData.existingUserMemAgentOnboardingIsRelevant === undefined;
        const hasGeneratedBeforeDeferredAccessState =
          cachedData.existingUserMemAgentOnboardingDeferredUntilAccess ===
            undefined &&
          cachedData.existingUserMemAgentOnboardingNeedsEvaluation === false &&
          cachedData.existingUserMemAgentOnboardingIsRelevant === false &&
          cachedData.hasCompletedExistingUserMemAgentIntroOnboarding !== true &&
          cachedData.hasCompletedExistingUserMemAgentFirstMessageOnboarding !==
            true;
        const existingUserMemAgentOnboardingNeedsEvaluation =
          (cachedData.existingUserMemAgentOnboardingNeedsEvaluation ??
            hasLegacyExistingUserMemAgentOnboardingState) ||
          hasGeneratedBeforeDeferredAccessState;

        this.deviceOnboardingInfo = asyncResultModule.setReady({
          hasDismissedMeetingOnboardingSystemPermissions:
            cachedData.hasDismissedMeetingOnboardingSystemPermissions,
          hasDismissedVoiceOnboardingSystemPermissions:
            cachedData.hasDismissedVoiceOnboardingSystemPermissions,
          hasCompletedOrSkippedNativeAppDownload:
            cachedData.hasCompletedOrSkippedNativeAppDownload,
          proactiveTrialOnboardingIsRelevant:
            cachedData.proactiveTrialOnboardingIsRelevant ?? false,
          hasCompletedOrSkippedProactiveTrialOnboarding:
            cachedData.hasCompletedOrSkippedProactiveTrialOnboarding ?? false,
          proactiveTrialOnboardingDebugOverride:
            cachedData.proactiveTrialOnboardingDebugOverride ?? false,
          memAgentOnboardingIsRelevant:
            cachedData.memAgentOnboardingIsRelevant ?? false,
          hasCompletedMemAgentShortcutOnboarding:
            cachedData.hasCompletedMemAgentShortcutOnboarding ?? false,
          hasCompletedMemAgentFirstMessageOnboarding:
            cachedData.hasCompletedMemAgentFirstMessageOnboarding ?? false,
          quickThoughtsCaptureMethod:
            cachedData.quickThoughtsCaptureMethod ?? "voice",
          existingUserMemAgentOnboardingDeferredUntilAccess:
            cachedData.existingUserMemAgentOnboardingDeferredUntilAccess ??
            false,
          existingUserMemAgentOnboardingNeedsEvaluation,
          existingUserMemAgentOnboardingIsRelevant:
            cachedData.existingUserMemAgentOnboardingIsRelevant ?? false,
          hasCompletedExistingUserMemAgentIntroOnboarding:
            cachedData.hasCompletedExistingUserMemAgentIntroOnboarding ?? false,
          hasCompletedExistingUserMemAgentFirstMessageOnboarding:
            cachedData.hasCompletedExistingUserMemAgentFirstMessageOnboarding ??
            false,
        });
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.warn({
        message:
          "[PerDeviceOnboardingInfoObservable] Failed to hydrate onboarding info",
        info: { error: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.deviceOnboardingInfo = asyncResultModule.setError(err);
      });
    }
  }

  async initialize() {
    await this._hydrateOnboardingInfo();
  }

  resetState() {
    runInAction(() => {
      this.deviceOnboardingInfo = asyncResultModule.setInitial();
    });
  }
}
